import supabase from '../supabase/supabase';

const normalizeTransactionIds = (transactionIds) => {
  if (Array.isArray(transactionIds)) {
    return [...new Set(transactionIds.filter(Boolean))];
  }

  if (typeof transactionIds === 'string') {
    try {
      const parsed = JSON.parse(transactionIds);

      return Array.isArray(parsed)
        ? [...new Set(parsed.filter(Boolean))]
        : [];
    } catch {
      return [];
    }
  }

  return [];
};

const requireOrganizationId = (organizationId) => {
  if (!organizationId) {
    throw new Error('Select a business before continuing.');
  }

  return organizationId;
};

const requireAccountId = (coaId) => {
  if (!coaId) {
    throw new Error('Choose an account to reconcile.');
  }

  return coaId;
};

const requireStatementDate = (statementDate) => {
  if (!statementDate) {
    throw new Error('Enter the statement end date.');
  }

  return statementDate;
};

const requireEndingBalance = (endingBalance) => {
  if (
    endingBalance === ''
    || endingBalance === null
    || endingBalance === undefined
    || !Number.isFinite(Number(endingBalance))
  ) {
    throw new Error('Enter a valid statement closing balance.');
  }

  return Number(endingBalance);
};

class ReconciliationService {
  /**
   * Resolve the organization for legacy callers.
   *
   * New reconciliation flows should ALWAYS provide organizationId
   * explicitly so the currently selected organization is used.
   */
  async getAccountId() {
    const {
      data: { user }
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('organization_users')
      .select('organization_id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data?.organization_id) {
      throw new Error('No organization is available for this user.');
    }

    return data.organization_id;
  }

  /**
   * Verify that the current authenticated user belongs to the organization.
   *
   * This is an additional frontend service-layer guard.
   * Supabase RLS must still remain the authoritative security boundary.
   */
  async verifyOrganizationAccess(organizationId) {
    const organizationIdValue = requireOrganizationId(organizationId);

    const {
      data: { user }
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('organization_users')
      .select('organization_id')
      .eq('organization_id', organizationIdValue)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error('You do not have access to this organization.');
    }

    return true;
  }

  /**
   * Get Cash/Bank/Credit Card accounts available for reconciliation.
   */
  async getBankAccounts(organizationId) {
    const organizationIdValue = requireOrganizationId(organizationId);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        name,
        code,
        type,
        sub_type,
        is_active
      `)
      .eq('organization_id', organizationIdValue)
      .eq('is_active', true)
      .order('code', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      throw error;
    }

    return (data || [])
      .filter((account) => (
        account.type === 'Liability'
        || ['bank', 'cash'].some((value) => (
          (account.sub_type || '').toLowerCase().includes(value)
        ))
      ))
      .map((account) => ({
        ...account,
        account_name: account.name,
        account_type: account.type === 'Liability'
          ? 'credit_card'
          : 'checking',
        institution_name: 'Chart of Accounts',
        mask: null
      }));
  }

  /**
   * Get reconciliation history for the active organization.
   */
  async getReconciliations(filters = {}, organizationId) {
    const organizationIdValue = requireOrganizationId(organizationId);

    await this.verifyOrganizationAccess(organizationIdValue);

    let query = supabase
      .schema('accounting')
      .from('reconciliations')
      .select(`
        *,
        chart_of_accounts:account_id (
          id,
          organization_id,
          name,
          code,
          type,
          sub_type
        )
      `)
      .eq('organization_id', organizationIdValue)
      .in('status', ['RECONCILED', 'UNBALANCED'])
      .order('statement_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (
      filters.bank_connection_id
      && filters.bank_connection_id !== 'all'
    ) {
      query = query.eq('account_id', filters.bank_connection_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Reconciliation history query failed:', error);

      throw new Error(
        error.message || 'Unable to load reconciliation history.'
      );
    }

    return (data || []).map((reconciliation) => ({
      ...reconciliation,

      coa_id: reconciliation.account_id,

      transaction_ids: normalizeTransactionIds(
        reconciliation.transaction_ids
      ),

      bank_connection: {
        id: reconciliation.account_id,
        account_name:
          reconciliation.chart_of_accounts?.name || 'Unknown account',
        account_type:
          reconciliation.chart_of_accounts?.type === 'Liability'
            ? 'credit_card'
            : 'checking',
        institution_name: 'Chart of Accounts',
        mask: null
      }
    }));
  }

  /**
   * Create an unfinished reconciliation when the ledger does not
   * currently match the statement balance.
   */
  async recordUnbalancedLedgerReconciliation({
    organizationId,
    coaId,
    statementDate,
    endingBalance
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);
    const accountId = requireAccountId(coaId);
    const date = requireStatementDate(statementDate);
    const balance = requireEndingBalance(endingBalance);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('record_unbalanced_reconciliation', {
        p_organization_id: organizationIdValue,
        p_account_id: accountId,
        p_statement_date: date,
        p_ending_balance: balance
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Load transactions saved on a reconciliation.
   *
   * IMPORTANT:
   * Transaction IDs alone are not sufficient for tenant isolation.
   * We also require organizationId and account ownership.
   */
  async getReconciliationTransactions(
    transactionIds,
    organizationId
  ) {
    const organizationIdValue = requireOrganizationId(organizationId);
    const ids = normalizeTransactionIds(transactionIds);

    if (ids.length === 0) {
      throw new Error(
        'This reconciliation has no saved transactions to edit.'
      );
    }

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .from('transactions')
      .select(`
        id,
        organization_id,
        account_id,
        date,
        description,
        debit,
        credit,
        status,
        created_at
      `)
      .eq('organization_id', organizationIdValue)
      .in('id', ids)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const transactions = data || [];

    if (transactions.length !== ids.length) {
      throw new Error(
        'Some transactions from this reconciliation are no longer available.'
      );
    }

    return transactions.map((transaction) => ({
      ...transaction,
      coa_id: transaction.account_id
    }));
  }

  /**
   * Finalize an existing reconciliation with selected transactions.
   */
  async completeEditedLedgerReconciliation({
    organizationId,
    reconciliationId,
    transactionIds
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);

    if (!reconciliationId) {
      throw new Error('A reconciliation record is required.');
    }

    const ids = normalizeTransactionIds(transactionIds);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('finalize_reconciliation', {
        p_organization_id: organizationIdValue,
        p_reconciliation_id: reconciliationId,
        p_transaction_ids: ids
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Resolve an unbalanced reconciliation after the user has
   * manually matched the required transactions.
   */
  async resolveUnbalancedLedgerReconciliation({
    organizationId,
    reconciliationId,
    transactionIds
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);

    if (!reconciliationId) {
      throw new Error('An unfinished reconciliation record is required.');
    }

    const ids = normalizeTransactionIds(transactionIds);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('finalize_reconciliation', {
        p_organization_id: organizationIdValue,
        p_reconciliation_id: reconciliationId,
        p_transaction_ids: ids
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Complete a reconciliation using explicitly selected transactions.
   */
  async completeSelectedLedgerReconciliation({
    organizationId,
    coaId,
    statementDate,
    endingBalance,
    transactionIds
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);
    const accountId = requireAccountId(coaId);
    const date = requireStatementDate(statementDate);
    const balance = requireEndingBalance(endingBalance);
    const ids = normalizeTransactionIds(transactionIds);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('complete_reconciliation', {
        p_organization_id: organizationIdValue,
        p_account_id: accountId,
        p_statement_date: date,
        p_ending_balance: balance,
        p_transaction_ids: ids
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Complete an automatically balanced reconciliation.
   *
   * No transaction IDs are supplied because the RPC determines
   * the transactions that belong to the reconciliation period.
   */
  async completeLedgerReconciliation({
    organizationId,
    coaId,
    statementDate,
    endingBalance
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);
    const accountId = requireAccountId(coaId);
    const date = requireStatementDate(statementDate);
    const balance = requireEndingBalance(endingBalance);

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('complete_reconciliation', {
        p_organization_id: organizationIdValue,
        p_account_id: accountId,
        p_statement_date: date,
        p_ending_balance: balance,
        p_transaction_ids: null
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Reverse a completed reconciliation.
   */
  async undoReconciliation({
    organizationId,
    reconciliationId
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);

    if (!reconciliationId) {
      throw new Error('A reconciliation record is required.');
    }

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('reverse_reconciliation', {
        p_organization_id: organizationIdValue,
        p_reconciliation_id: reconciliationId
      });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Cancel an unfinished reconciliation.
   */
  async cancelUnbalancedReconciliation({
    organizationId,
    reconciliationId
  }) {
    const organizationIdValue = requireOrganizationId(organizationId);

    if (!reconciliationId) {
      throw new Error(
        'An unfinished reconciliation record is required.'
      );
    }

    await this.verifyOrganizationAccess(organizationIdValue);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('reverse_reconciliation', {
        p_organization_id: organizationIdValue,
        p_reconciliation_id: reconciliationId
      });

    if (error) {
      throw error;
    }

    return data;
  }
}

export const reconciliationService = new ReconciliationService();